import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, IconButton, Typography, TablePagination, TextField, useTheme, useMediaQuery, Card, CardContent, Stack, InputAdornment, Tooltip
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, Search as SearchIcon, FileDownload as FileDownloadIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import * as XLSX from 'xlsx';
import bancoService, { Banco, BancoPagedResult } from '../../services/bancoService';
import {
  loadTesoreriaFiltros,
  storedNumber,
  storedString,
  TESORERIA_FILTROS_KEYS,
  usePersistTesoreriaFiltros,
} from '../../utils/tesoreriaFiltrosStorage';

interface BancosFiltrosStored {
  searchTerm?: string;
  rowsPerPage?: number;
}

const Bancos: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const loadStored = () => loadTesoreriaFiltros<BancosFiltrosStored>(TESORERIA_FILTROS_KEYS.bancos);

  const [bancosData, setBancosData] = useState<BancoPagedResult>({
    items: [], totalCount: 0, pageNumber: 1, pageSize: 10, totalPages: 0
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(() => storedNumber(loadStored(), 'rowsPerPage', 10));
  const [searchTerm, setSearchTerm] = useState(() => storedString(loadStored(), 'searchTerm', ''));
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [loading, setLoading] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);

  const fetchBancos = async (pageNumber: number, pageSize: number, search?: string) => {
    try {
      setLoading(true);
      const rawSearch = (search ?? '').trim();
      const compact = rawSearch.replace(/\s+/g, '');
      const isNumericSearch = compact !== '' && /^\d+$/.test(compact);
      
      if (isNumericSearch) {
        try {
          const nro = Number(compact);
          const banco = await bancoService.getBanco(nro);
          const adjustedData = {
            items: banco ? [banco] : [],
            totalCount: banco ? 1 : 0,
            pageNumber: 1,
            pageSize,
            totalPages: 1
          };
          setBancosData(adjustedData);
          return;
        } catch (error: any) {
          if (error?.response?.status === 404) {
            setBancosData({ items: [], totalCount: 0, pageNumber: 1, pageSize, totalPages: 0 });
            return;
          }
          console.error('Error al buscar por nroBanco:', error);
          setBancosData({ items: [], totalCount: 0, pageNumber: 1, pageSize, totalPages: 0 });
          return;
        }
      }
      
      const response = await bancoService.getBancos({
        pageNumber: pageNumber,
        pageSize,
        searchTerm: rawSearch || undefined
      });
      setBancosData(response);
    } catch (error: any) {
      console.error('Error al cargar bancos:', error);
      enqueueSnackbar('Error al cargar bancos', { variant: 'error' });
      setBancosData({ items: [], totalCount: 0, pageNumber: 1, pageSize, totalPages: 0 });
    } finally {
      setLoading(false);
    }
  };

  usePersistTesoreriaFiltros(
    TESORERIA_FILTROS_KEYS.bancos,
    { searchTerm, rowsPerPage },
    [searchTerm, rowsPerPage]
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchBancos(page + 1, rowsPerPage, searchTerm);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [page, rowsPerPage, searchTerm]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const fetchAllBancosForExport = async (): Promise<Banco[]> => {
    const rawSearch = searchTerm.trim();
    const compact = rawSearch.replace(/\s+/g, '');
    const isNumericSearch = compact !== '' && /^\d+$/.test(compact);

    if (isNumericSearch) {
      try {
        const banco = await bancoService.getBanco(Number(compact));
        return banco ? [banco] : [];
      } catch (error: any) {
        if (error?.response?.status === 404) return [];
        throw error;
      }
    }

    const pageSize = 20;
    const allItems: Banco[] = [];
    let pageNumber = 1;
    let totalPages = 1;

    while (pageNumber <= totalPages) {
      const response = await bancoService.getBancos({
        pageNumber,
        pageSize,
        searchTerm: rawSearch || undefined,
      });
      allItems.push(...response.items);
      totalPages = Math.max(response.totalPages || 1, 1);
      if (!response.items.length) break;
      pageNumber++;
    }

    return allItems;
  };

  const handleExportExcel = async () => {
    if (bancosData.totalCount === 0) {
      enqueueSnackbar('No hay datos para exportar.', { variant: 'warning' });
      return;
    }

    try {
      setExportingExcel(true);
      enqueueSnackbar('Obteniendo datos para exportar...', { variant: 'info' });
      const items = await fetchAllBancosForExport();

      if (!items.length) {
        enqueueSnackbar('No hay datos para exportar.', { variant: 'warning' });
        return;
      }

      const datos = items.map((b) => ({
        'Nro. Banco': b.nroBanco,
        Nombre: b.nombreBanco?.trim() || '',
      }));

      const ws = XLSX.utils.json_to_sheet(datos);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Bancos');
      const fileName = `Bancos_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
      enqueueSnackbar(`Exportado correctamente (${items.length} registros)`, { variant: 'success' });
    } catch (error: any) {
      console.error('Error al exportar:', error);
      enqueueSnackbar(error?.response?.data?.message || 'Error al exportar a Excel', { variant: 'error' });
    } finally {
      setExportingExcel(false);
    }
  };

  const handleDelete = async (nroBanco: number) => {
    if (window.confirm('¿Está seguro de eliminar este banco?')) {
      try {
        await bancoService.deleteBanco(nroBanco);
        enqueueSnackbar('Banco eliminado exitosamente', { variant: 'success' });
        fetchBancos(page + 1, rowsPerPage, searchTerm);
      } catch (error: any) {
        console.error('Error al eliminar banco:', error);
        enqueueSnackbar(error?.response?.data?.message || 'Error al eliminar banco', { variant: 'error' });
      }
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card 
        sx={{ 
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack 
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between" 
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 600,
                color: '#1976d2'
              }}
            >
              Bancos
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Button
                variant="outlined"
                color="primary"
                startIcon={<FileDownloadIcon />}
                onClick={handleExportExcel}
                disabled={loading || exportingExcel || bancosData.totalCount === 0}
                sx={{
                  borderRadius: 2,
                  px: 2,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 500,
                }}
              >
                {exportingExcel ? 'Exportando...' : 'Exportar Excel'}
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => navigate('/tesoreria/bancos/nuevo')}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 500,
                  boxShadow: '0 2px 4px rgba(25, 118, 210, 0.3)',
                  '&:hover': {
                    boxShadow: '0 4px 8px rgba(25, 118, 210, 0.4)',
                  }
                }}
              >
                Nuevo Banco
              </Button>
            </Stack>
          </Stack>

          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Buscar por nombre o número de banco..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: '#fafafa',
                  '&:hover': {
                    bgcolor: '#f5f5f5',
                  },
                  '&.Mui-focused': {
                    bgcolor: '#ffffff',
                  }
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#666' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {isMobile ? (
            <Stack spacing={2}>
              {bancosData.items.map((banco) => (
                <Card 
                  key={banco.nroBanco}
                  sx={{
                    borderRadius: 2,
                    border: '1px solid #e0e0e0',
                    '&:hover': {
                      boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                    },
                  }}
                >
                  <CardContent>
                    <Stack spacing={1.5}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {banco.nombreBanco?.trim() || '-'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Nro: {banco.nroBanco}
                          </Typography>
                        </Box>
                      </Stack>
                      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                        <Tooltip title="Editar banco">
                          <IconButton
                            onClick={() => navigate(`/tesoreria/bancos/${banco.nroBanco}`)}
                            size="small"
                            sx={{
                              bgcolor: '#2196f3',
                              color: '#fff',
                              borderRadius: 1.5,
                              '&:hover': { bgcolor: '#1976d2' }
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar banco">
                          <IconButton
                            onClick={() => handleDelete(banco.nroBanco)}
                            size="small"
                            sx={{
                              bgcolor: '#f44336',
                              color: '#fff',
                              borderRadius: 1.5,
                              '&:hover': { bgcolor: '#d32f2f' }
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
              <TablePagination
                component="div"
                count={bancosData.totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
                labelRowsPerPage="Filas por página"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`}
              />
            </Stack>
          ) : (
            <TableContainer 
              component={Paper}
              sx={{
                borderRadius: 2,
                boxShadow: 'none',
                border: '1px solid #e0e0e0',
              }}
            >
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Nro. Banco</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Nombre</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bancosData.items.map((banco) => (
                    <TableRow 
                      key={banco.nroBanco}
                      sx={{
                        '&:hover': {
                          bgcolor: '#f9f9f9',
                        },
                      }}
                    >
                      <TableCell sx={{ fontWeight: 500 }}>{banco.nroBanco}</TableCell>
                      <TableCell>{banco.nombreBanco?.trim() || '-'}</TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={0.5} justifyContent="center">
                          <Tooltip title="Editar banco">
                            <IconButton
                              onClick={() => navigate(`/tesoreria/bancos/${banco.nroBanco}`)}
                              size="small"
                              sx={{
                                bgcolor: '#2196f3',
                                color: '#fff',
                                borderRadius: 1.5,
                                width: 32,
                                height: 32,
                                '&:hover': { 
                                  bgcolor: '#1976d2',
                                  transform: 'scale(1.1)',
                                },
                                transition: 'all 0.2s'
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Eliminar banco">
                            <IconButton
                              onClick={() => handleDelete(banco.nroBanco)}
                              size="small"
                              sx={{
                                bgcolor: '#f44336',
                                color: '#fff',
                                borderRadius: 1.5,
                                width: 32,
                                height: 32,
                                '&:hover': { 
                                  bgcolor: '#d32f2f',
                                  transform: 'scale(1.1)',
                                },
                                transition: 'all 0.2s'
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={bancosData.totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
                labelRowsPerPage="Filas por página"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`}
                sx={{
                  borderTop: '1px solid #e0e0e0',
                }}
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default Bancos;

